import { useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faPlay, faXmark } from "@fortawesome/free-solid-svg-icons";
import type { AboutMediaItem } from "./about-media";

type AboutMediaFrameProps = {
  item: AboutMediaItem;
  className?: string;
  onClick: () => void;
};

export default function AboutMediaFrame({ item, className, onClick }: AboutMediaFrameProps) {
  const hasMedia = Boolean(item.imageSrc || item.videoSrc);

  return (
    <button
      type="button"
      className={`about-media-frame${hasMedia ? " about-media-frame--ready" : ""}${className ? ` ${className}` : ""}`}
      onClick={onClick}
      aria-label={hasMedia ? `View larger: ${item.caption}` : item.placeholderLabel}
    >
      {item.imageSrc ? (
        <img className="about-media-frame-image" src={item.imageSrc} alt="" loading="lazy" />
      ) : (
        <span className="about-media-frame-placeholder" aria-hidden="true">
          <span className="about-screenshot-placeholder-icon">
            <FontAwesomeIcon icon={faImage} />
          </span>
          <span className="about-media-frame-placeholder-label">{item.placeholderLabel}</span>
        </span>
      )}
      {hasMedia ? (
        <span className="about-media-frame-expand" aria-hidden="true">
          {item.videoSrc ? <FontAwesomeIcon icon={faPlay} /> : null}
          <span className="about-media-frame-expand-text">View full size</span>
        </span>
      ) : null}
    </button>
  );
}

type AboutMediaLightboxProps = {
  item: AboutMediaItem;
  open: boolean;
  onClose: () => void;
};

/** Full-page lightbox for a single screenshot or clip. */
export function AboutMediaLightbox({ item, open, onClose }: AboutMediaLightboxProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const reduceMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !open) return;

    if (item.videoSrc && !reduceMotion) {
      void video.play().catch(() => {
        /* autoplay blocked; controls remain available */
      });
    } else {
      video.pause();
    }

    return () => video.pause();
  }, [open, item.videoSrc, reduceMotion]);

  const onDialogClose = () => {
    videoRef.current?.pause();
    onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className="about-media-lightbox"
      onClose={onDialogClose}
      onCancel={(event) => {
        event.preventDefault();
        onDialogClose();
      }}
      onClick={(event) => {
        if (event.target === dialogRef.current) onDialogClose();
      }}
    >
      <div className="about-media-lightbox-inner" role="document">
        <button
          type="button"
          className="about-media-lightbox-close"
          onClick={onDialogClose}
          aria-label="Close preview"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>

        <div className="about-media-lightbox-stage">
          {item.videoSrc && !reduceMotion ? (
            <video
              ref={videoRef}
              key={item.videoSrc}
              className="about-media-lightbox-video"
              src={item.videoSrc}
              poster={item.imageSrc}
              controls
              playsInline
              muted
            />
          ) : item.imageSrc ? (
            <img className="about-media-lightbox-image" src={item.imageSrc} alt="" />
          ) : (
            <div className="about-media-lightbox-missing">
              <FontAwesomeIcon icon={faImage} />
              <p>{item.placeholderLabel}</p>
            </div>
          )}
        </div>

        <p className="about-media-lightbox-caption">{item.caption}</p>
      </div>
    </dialog>
  );
}
